import { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { get } from 'api';

export default function PayrollPolicyView() {
  // const navigate = useNavigate();
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentPolicy = async () => {
    try {
      const res = await get('admin/currentPayrollPolicy');
      if (res.status === 200) {
        setCurrentPolicy(res.data);
      } else {
        setCurrentPolicy(null);
      }
    } catch (err) {
      console.log(err);
      setCurrentPolicy(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentPolicy();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Skeleton height={60} />
        <Skeleton height={200} sx={{ mt: 3 }} />
      </Container>
    );
  }

  if (!currentPolicy) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center">
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Payroll Policy
          </Typography>
          <Typography color="text.secondary" mb={4}>
            No active payroll policy configured yet.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              // navigate('/policy-create');
            }}
          >
            Create Policy
          </Button>
        </Box>
      </Container>
    );
  }

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h6" fontWeight={600} mb={3}>
        {title}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          backgroundColor: 'grey.50',
        }}
      >
        {children}
      </Paper>
    </Box>
  );

  const Field = ({ label, value }) => (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={0.5}>
        {label}
      </Typography>
      <Typography variant="subtitle1" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Payroll Policy
          </Typography>
          <Typography color="text.secondary">
            Manage company salary structure and statutory configuration
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<AddIcon />}>
          Create Policy
        </Button>
      </Box>

      {/* POLICY HERO */}
      <Paper
        elevation={1}
        sx={{
          p: 4,
          borderRadius: 3,
          mb: 6,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {currentPolicy.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Effective from {new Date(currentPolicy.effectiveFrom).toLocaleDateString()}
            </Typography>
          </Box>

          {currentPolicy.isActive && <Chip label="Active Policy" color="success" />}
        </Box>
      </Paper>

      {/* SALARY STRUCTURE */}
      <Section title="Salary Structure (% of CTC)">
        <Grid container spacing={4}>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Field label="Basic" value={`${currentPolicy.basicPercent}%`} />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Field label="HRA (% of Basic)" value={`${currentPolicy.hraPercent}%`} />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Field label="Special Allowance" value={`${currentPolicy.specialAllowancePercent}%`} />
          </Grid>
        </Grid>
      </Section>

      {/* STATUTORY */}
      <Section title="Statutory Deductions">
        <Grid container spacing={4}>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Field
              label="Professional Tax"
              value={
                currentPolicy.professionalTaxEnabled ? (
                  `₹ ${currentPolicy.professionalTaxAmount}`
                ) : (
                  <Chip label="Disabled" size="small" />
                )
              }
            />
          </Grid>

          <Grid item size={{ xs: 12, md: 4 }}>
            <Field
              label="Provident Fund (PF)"
              value={
                currentPolicy.pfEnabled ? (
                  `${currentPolicy.pfEmployeePercent}% / ${currentPolicy.pfEmployerPercent}%`
                ) : (
                  <Chip label="Disabled" size="small" />
                )
              }
            />
          </Grid>

          <Grid item size={{ xs: 12, md: 4 }}>
            <Field
              label="ESIC"
              value={
                currentPolicy.esicEnabled ? (
                  `${currentPolicy.esicEmployeePercent}% / ${currentPolicy.esicEmployerPercent}%`
                ) : (
                  <Chip label="Disabled" size="small" />
                )
              }
            />
          </Grid>
        </Grid>
      </Section>

      {/* LEAVES */}
      <Section title="Leave Rules">
        <Grid container spacing={4}>
          <Grid item size={{ xs: 12, md: 4 }}>
            <Field label="Sick Leave / Year" value={currentPolicy.slPerYear} />
          </Grid>

          <Grid item size={{ xs: 12, md: 4 }}>
            <Field label="Paid Leave / Year" value={currentPolicy.plPerYear} />
          </Grid>

          <Grid item size={{ xs: 12, md: 4 }}>
            <Field
              label="Carry Forward"
              value={
                currentPolicy.slCarryForward
                  ? `Yes (Max: ${currentPolicy.maxSlCarryForward})`
                  : 'No'
              }
            />
          </Grid>
        </Grid>
      </Section>

      <Divider sx={{ my: 6 }} />

      {/* EFFECTIVE PERIOD */}
      <Box>
        <Typography variant="body2" color="text.secondary">
          Policy Validity
        </Typography>
        <Typography fontWeight={600}>
          {new Date(currentPolicy.effectiveFrom).toLocaleDateString()}
          {currentPolicy.effectiveTo &&
            ` - ${new Date(currentPolicy.effectiveTo).toLocaleDateString()}`}
        </Typography>
      </Box>
    </Container>
  );
}

// import { useEffect, useState } from 'react';
// import AddIcon from '@mui/icons-material/Add';
// import { Box, Button, Card, CardContent, Chip, Container, Grid, Typography } from '@mui/material';
// import { get } from 'api';

// export default function PayrollPolicyView() {
//   const [currentPolicy, setCurrentPolicy] = useState({});
//   const fetchCurrentPolicy = async () => {
//     const res = await get('admin/currentPayrollPolicy');
//     if (res.status == 200) {
//       setCurrentPolicy(res.data);
//     } else {
//       setCurrentPolicy(null);
//     }
//   };

//   useEffect(() => {
//     fetchCurrentPolicy();
//   }, []);
//   if (!currentPolicy) {
//     return (
//       <Container maxWidth="lg" sx={{ py: 6 }}>
//         <Typography variant="h4" fontWeight={600} mb={4}>
//           Payroll Policy
//         </Typography>

//         <Card sx={{ p: 6, textAlign: 'center' }}>
//           <Typography variant="h6" mb={2}>
//             No Payroll Policy Found
//           </Typography>

//           <Button variant="contained" startIcon={<AddIcon />} onClick={() => {}}>
//             Create Policy
//           </Button>
//         </Card>
//       </Container>
//     );
//   }

//   return (
//     <Container maxWidth="lg" sx={{ py: 6 }}>
//       {/* Header */}
//       <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
//         <Typography variant="h4" fontWeight={600}>
//           Payroll Policy
//         </Typography>

//         <Button variant="contained" startIcon={<AddIcon />} onClick={() => {}}>
//           Create Policy
//         </Button>
//       </Box>

//       {/* currentPolicy NAME */}
//       <Card sx={{ mb: 4 }}>
//         <CardContent>
//           <Box display="flex" justifyContent="space-between">
//             <Typography variant="h6">{currentPolicy.name}</Typography>

//             {currentPolicy.isActive && <Chip label="Active" color="success" size="small" />}
//           </Box>
//         </CardContent>
//       </Card>

//       {/* SALARY STRUCTURE */}
//       <Card sx={{ mb: 4 }}>
//         <CardContent>
//           <Typography variant="h6" mb={3}>
//             Salary Structure (% of CTC)
//           </Typography>

//           <Grid container spacing={3}>
//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">Basic</Typography>
//               <Typography variant="h6">{currentPolicy.basicPercent}%</Typography>
//             </Grid>

//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">HRA (% of Basic)</Typography>
//               <Typography variant="h6">{currentPolicy.hraPercent}%</Typography>
//             </Grid>

//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">Special Allowance</Typography>
//               <Typography variant="h6">{currentPolicy.specialAllowancePercent}%</Typography>
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>

//       {/* STATUTORY */}
//       <Card sx={{ mb: 4 }}>
//         <CardContent>
//           <Typography variant="h6" mb={3}>
//             Statutory Deductions
//           </Typography>

//           <Grid container spacing={3}>
//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">Professional Tax</Typography>
//               <Typography>
//                 {currentPolicy.professionalTaxEnabled
//                   ? `₹ ${currentPolicy.professionalTaxAmount}`
//                   : 'Disabled'}
//               </Typography>
//             </Grid>

//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">PF</Typography>
//               <Typography>
//                 {currentPolicy.pfEnabled
//                   ? `${currentPolicy.pfEmployeePercent}% / ${currentPolicy.pfEmployerPercent}%`
//                   : 'Disabled'}
//               </Typography>
//             </Grid>

//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">ESIC</Typography>
//               <Typography>
//                 {currentPolicy.esicEnabled
//                   ? `${currentPolicy.esicEmployeePercent}% / ${currentPolicy.esicEmployerPercent}%`
//                   : 'Disabled'}
//               </Typography>
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>

//       {/* LEAVES */}
//       <Card sx={{ mb: 4 }}>
//         <CardContent>
//           <Typography variant="h6" mb={3}>
//             Leave Rules
//           </Typography>

//           <Grid container spacing={3}>
//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">Sick Leave / Year</Typography>
//               <Typography>{currentPolicy.slPerYear}</Typography>
//             </Grid>

//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">Paid Leave / Year</Typography>
//               <Typography>{currentPolicy.plPerYear}</Typography>
//             </Grid>

//             <Grid item size={{ xs: 4 }}>
//               <Typography variant="body2">Carry Forward</Typography>
//               <Typography>
//                 {currentPolicy.slCarryForward
//                   ? `Yes (Max: ${currentPolicy.maxSlCarryForward})`
//                   : 'No'}
//               </Typography>
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>

//       {/* EFFECTIVE PERIOD */}
//       <Card>
//         <CardContent>
//           <Typography variant="h6" mb={3}>
//             Effective Period
//           </Typography>

//           <Typography>
//             {new Date(currentPolicy.effectiveFrom).toLocaleDateString()}{' '}
//             {currentPolicy.effectiveTo &&
//               ` - ${new Date(currentPolicy.effectiveTo).toLocaleDateString()}`}
//           </Typography>
//         </CardContent>
//       </Card>
//     </Container>
//   );
// }
